import { useMutation, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { HStack, View, Box, useTheme } from 'native-base';

import { Button, Text } from '@pimm/base';
import { AlertCircleIcon } from '@pimm/base';
import { UpdateEodReviewRequest } from '@pimm/services/lib/store-inspections';
import { ThumbsUpIcon } from '../icons/thumbs-up';
import { UpdateEodReview } from '@pimm/services/lib/store-inspections/services';

type ReviewVerifyConfirmationProps = {
  isAuthorized?: boolean;
  onConfirm: (_callback: UseMutationResult<any, Error, UpdateEodReviewRequest>) => (_confirm?: boolean) => void;
};

export const ReviewVerifyConfirmation = ({ isAuthorized, ..._props }: ReviewVerifyConfirmationProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const updateEodReview = useMutation<any, Error, UpdateEodReviewRequest>({ mutationFn: UpdateEodReview });

  const handleConfirm = (_confirm?: boolean) => {
    _props?.onConfirm(updateEodReview)(_confirm);
  };

  return (
    <View position="relative" px={6} pt={5} minHeight="200px">
      <HStack alignItems="flex-start" justifyContent="space-between" mb={4}>
        <Box rounded="full" boxSize={12} alignItems="center" justifyContent="center" bg={isAuthorized ? 'blueLight.100' : 'error.100'}>
          {isAuthorized ? <ThumbsUpIcon size={6} color={colors.blueLight[600]} /> : <AlertCircleIcon size={6} color={colors.error[600]} />}
        </Box>
      </HStack>

      <Box mt={2} mb={isAuthorized ? 2 : 3}>
        <Text size="xl" color="gray.900" fontWeight={500} mb={2} lineHeight="sm" onPress={() => handleConfirm()}>
          {isAuthorized ? t('store_inspections:verify_reports') : t('store_inspections:permission_denied')}
        </Text>
        <Text size="md" color="gray.600" fontWeight={400} mb={3} lineHeight="md">
          {isAuthorized ? t('store_inspections:verify_reports_message') : t('store_inspections:permission_denied_message')}
        </Text>
      </Box>

      {isAuthorized && (
        <HStack space={2} pt={1} pb={5}>
          <Button
            flex={1}
            size="lg"
            variant="unstyled"
            shadow={0}
            borderColor="gray.300"
            bg="white"
            isDisabled={updateEodReview.isLoading}
            onPress={() => handleConfirm()}
          >
            {t('app:cancel')}
          </Button>

          <Button
            flex={1}
            size="lg"
            shadow={0}
            borderColor="primary.600"
            isDisabled={updateEodReview.isSuccess}
            isLoading={updateEodReview.isLoading}
            onPress={() => handleConfirm(true)}
          >
            {t('app:confirm')}
          </Button>
        </HStack>
      )}
    </View>
  );
};
